import type {
  SquawkChargePublic,
  SquawkChargeUpsert,
  SquawkDetailPublic,
  SquawkUpdate,
} from "@common/client"

const FIELDS: ReadonlySet<string> = new Set<keyof SquawkUpdate>([
  "description",
  "severity",
  "manual_reference",
  "resolution",
  "ata_chapter",
  "ata_subchapter",
  "ata_zone",
  "charges",
])

export function isPredictableSquawkUpdate(body: SquawkUpdate): boolean {
  return (
    Object.keys(body).every((key) => FIELDS.has(key)) &&
    (body.charges ?? []).every((charge) => Boolean(charge.id))
  )
}

function given<T>(next: T | undefined, current: T): T {
  return next === undefined ? current : next
}

function decimal(
  next: number | string | null | undefined,
  current: string | null,
): string | null {
  if (next === undefined) return current
  return next === null ? null : String(next)
}

function chargeWith(
  charge: SquawkChargePublic,
  upsert: SquawkChargeUpsert,
): SquawkChargePublic {
  return {
    ...charge,
    description: given(upsert.description, charge.description),
    type: upsert.type,
    part_number: given(upsert.part_number, charge.part_number),
    purchase_unit_price: decimal(
      upsert.purchase_unit_price,
      charge.purchase_unit_price,
    ),
    sale_unit_price: decimal(upsert.sale_unit_price, charge.sale_unit_price),
    currency: given(upsert.currency, charge.currency),
    quantity: decimal(upsert.quantity, charge.quantity),
    unit_of_measure: given(upsert.unit_of_measure, charge.unit_of_measure),
    tax_amount: decimal(upsert.tax_amount, charge.tax_amount),
    lead_time: given(upsert.lead_time, charge.lead_time),
    cost_quantity: decimal(upsert.cost_quantity, charge.cost_quantity),
  }
}

function chargesWith(
  charges: SquawkChargePublic[],
  upserts: SquawkChargeUpsert[],
): SquawkChargePublic[] {
  const byId = new Map(charges.map((charge) => [charge.id, charge]))
  return upserts.flatMap((upsert) => {
    const charge = upsert.id ? byId.get(upsert.id) : undefined
    return charge ? [chargeWith(charge, upsert)] : []
  })
}

export function squawkWithUpdate(
  squawk: SquawkDetailPublic,
  body: SquawkUpdate,
): SquawkDetailPublic {
  return {
    ...squawk,
    description: body.description ?? squawk.description,
    severity: given(body.severity, squawk.severity),
    manual_reference: given(body.manual_reference, squawk.manual_reference),
    resolution: given(body.resolution, squawk.resolution),
    ata_chapter: given(body.ata_chapter, squawk.ata_chapter),
    ata_subchapter: given(body.ata_subchapter, squawk.ata_subchapter),
    ata_zone: given(body.ata_zone, squawk.ata_zone),
    active_charges: body.charges
      ? chargesWith(squawk.active_charges, body.charges)
      : squawk.active_charges,
  }
}
